'use client';

import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { SliceComponentProps } from '@prismicio/react';
import { useMenuContext } from '@/context/MenuContext';
import {
  SubmenuSliceAwardsStrip,
  SubmenuSliceCredits,
  SubmenuSliceModernSubmenu,
} from '../../../prismicio-types';

/**
 * Props for `Submenu`.
 */
interface IContextProps {
  isFooter?: boolean;
  isShown?: boolean;
}

export type SubmenuProps = SliceComponentProps<
  Content.SubmenuSlice,
  IContextProps
> & {
  toggleSideBar: () => void;
  togglePopOverMenu: (key: string) => void;
};

/**
 * Component for "Submenu" Slices.
 */
const Submenu = (props: SubmenuProps): JSX.Element => {
  const { slice, context } = props;
  const { toggleSideBar, togglePopOverMenu } = useMenuContext();

  const renderSliceVariation = () => {
    switch (slice.variation) {
      case 'modernSubmenu':
        return modernSubmenuVariation({
          ...props,
          toggleSideBar,
          togglePopOverMenu,
        });
      case 'awardsStrip':
        return context.isShown ? awardStripVariation(props) : null;
      case 'credits':
        return creditsVariation(props);
      default:
        return nonCreditsVariation({
          ...props,
          toggleSideBar,
          togglePopOverMenu,
        });
    }
  };

  return (
    <div
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="inline-block"
    >
      {renderSliceVariation()}
    </div>
  );
};

const modernSubmenuVariation = ({
  slice,
  toggleSideBar,
  togglePopOverMenu,
}: SubmenuProps & { toggleSideBar: () => void }) => {
  const data = slice as SubmenuSliceModernSubmenu;
  const closeMenu = () => {
    toggleSideBar();
    if ('tag' in slice.primary && slice.primary.tag) {
      togglePopOverMenu(slice.primary.tag);
    }
  };
  return (
    <div className="flex items-center justify-start gap-10">
      <div className="flex flex-col gap-5 text-white text-lg">
        {data.primary.element.map((item, index) => {
          return 'element_link' in item ? (
            <PrismicNextLink
              key={`submenu_${index.toString()}`}
              field={item.element_link}
              onClick={closeMenu}
              className="hover:text-primary-green transition-colors"
            >
              {'element_label' in item && <span>{item.element_label}</span>}
            </PrismicNextLink>
          ) : null;
        })}
      </div>
      {data.primary.velx.url && data.primary.velx_link.link_type !== 'Any' ? (
        <PrismicNextLink
          field={data.primary.velx_link}
          onClick={closeMenu}
          className="p-6 rounded-xl"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 35, 43, 0) 0%, #00232B 100%)',
          }}
        >
          <PrismicNextImage field={data.primary.velx} />
        </PrismicNextLink>
      ) : data.primary.velx.url ? (
        <div
          className="p-6 rounded-xl"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 35, 43, 0) 0%, #00232B 100%)',
          }}
        >
          <PrismicNextImage field={data.primary.velx} />
        </div>
      ) : null}
    </div>
  );
};

const creditsVariation = ({ slice }: SubmenuProps) => {
  const data = slice as SubmenuSliceCredits;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      {data.primary.element.map((item, index) => {
        return (
          <PrismicNextLink
            key={`submenu_${index.toString()}`}
            field={item.element_link}
            className="text-sm text-white/60 hover:text-white transition-colors"
          >
            {item.element_label}
          </PrismicNextLink>
        );
      })}
    </div>
  );
};

const nonCreditsVariation = ({
  slice,
  context,
  toggleSideBar,
  togglePopOverMenu,
}: SubmenuProps & { toggleSideBar: () => void }) => {
  const closeMenu = () => {
    toggleSideBar();
    if ('tag' in slice.primary && slice.primary.tag) {
      togglePopOverMenu(slice.primary.tag);
    }
  };
  return (
    <div
      className={`flex flex-col text-white text-sm ${context.isFooter ? 'gap-4' : 'gap-6 md:gap-4 lg:max-w-[427px]'}`}
    >
      {'title' in slice.primary && slice.primary.title && (
        <div
          className={`max-w-80 ${context.isFooter ? 'text-base font-semibold pb-2' : 'font-extrabold'}`}
        >
          {slice.primary.title}
        </div>
      )}
      {slice.variation === 'withDescription' &&
        'description' in slice.primary &&
        slice.primary.description && (
          <div className="font-normal">{slice.primary.description}</div>
        )}
      {'element' in slice.primary &&
        slice.primary.element &&
        slice.primary.element.map((item, index) => {
          return 'element_link' in item ? (
            <PrismicNextLink
              className={`flex gap-4 md:gap-3 items-center hover:underline ${context.isFooter ? 'text-sm' : 'text-base'}`}
              key={`submenu_${index.toString()}`}
              field={item.element_link}
              onClick={closeMenu}
            >
              {'icon' in item && item.icon && (
                <PrismicNextImage field={item.icon} />
              )}
              {'element_label' in item && <span>{item.element_label}</span>}
            </PrismicNextLink>
          ) : null;
        })}
    </div>
  );
};

const awardStripVariation = ({ slice, context }: SubmenuProps) => {
  const data = slice as SubmenuSliceAwardsStrip;
  return (
    <div className="flex flex-wrap items-center gap-3 lg:gap-5 justify-between w-full">
      {data.primary.element.map((item, index) => (
        <PrismicNextImage
          key={`award_${index}`}
          data-footer={context.isFooter}
          field={item.award}
          className="object-contain filter grayscale"
        />
      ))}
    </div>
  );
};

export default Submenu;
